// Lib partagée de gestion du thème (clair / sombre / système).
// Source de vérité : le cookie `theme` (light|dark), lu côté serveur par les
// layouts pour poser data-theme sur <html> (anti-FOUC). Absence de cookie =
// mode système : data-theme="system", le navigateur suit prefers-color-scheme.

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const COOKIE = 'theme';
const ONE_YEAR = 60 * 60 * 24 * 365;

// = ThemeHelper::THEME_COLORS côté Rails (fond --surface-page par thème).
const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#ffffff',
  dark: '#121212',
};

export function getPreference(): ThemePreference {
  const match = document.cookie.match(/(?:^|;\s*)theme=(light|dark)(?:;|$)/);
  return (match?.[1] as ThemePreference) ?? 'system';
}

export function resolvedTheme(): ResolvedTheme {
  const forced = document.documentElement.dataset.theme;
  if (forced === 'light' || forced === 'dark') return forced;

  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

// Sans light-dark() (Safari < 17.5), la page reste claire (`_tokens.scss`), quelle
// que soit la préférence.
function supportsLightDark(): boolean {
  return CSS.supports('color', 'light-dark(#fff, #000)');
}

export function renderedTheme(): ResolvedTheme {
  return supportsLightDark() ? resolvedTheme() : 'light';
}

export function setPreference(preference: ThemePreference): void {
  if (preference === 'system') {
    document.cookie = `${COOKIE}=; max-age=0; path=/`;
  } else {
    document.cookie = `${COOKIE}=${preference}; max-age=${ONE_YEAR}; path=/; samesite=lax`;
  }
  document.documentElement.dataset.theme = preference;

  updateThemeColorMetas();
  window.dispatchEvent(
    new CustomEvent<ResolvedTheme>('themechange', { detail: resolvedTheme() })
  );
}

// Même rendu que le partiel serveur `_theme_color` : une meta pour un thème forcé,
// deux metas media en mode système, pour que la barre du navigateur suive l'OS.
// Sans light-dark(), la barre doit rester claire comme la page : le serveur ne
// peut pas le savoir, le client recale ses metas au chargement.
function themeColorMetas(): [ResolvedTheme, string | null][] {
  const preference = getPreference();

  if (!supportsLightDark()) {
    return [['light', null]];
  }

  if (preference === 'system') {
    return [
      ['light', '(prefers-color-scheme: light)'],
      ['dark', '(prefers-color-scheme: dark)'],
    ];
  }

  return [[preference, null]];
}

export function updateThemeColorMetas(): void {
  const metas = themeColorMetas();

  document
    .querySelectorAll('meta[name="theme-color"]')
    .forEach((meta) => meta.remove());

  metas.forEach(([scheme, media]) => {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    if (media) meta.media = media;
    meta.content = THEME_COLORS[scheme];
    document.head.append(meta);
  });
}

// Une page rendue à nouveau par le retour arrière, depuis le bfcache ou le cache
// HTTP (que le navigateur ne revalide pas), garde le data-theme de son premier
// affichage, même si la préférence a changé depuis.
export function followPreferenceOnHistoryRestore(): void {
  window.addEventListener('pageshow', () => setPreference(getPreference()));
}
